import { humanizeValidationText, validationFieldLabel } from './validationMessages';
import { privateFileUrls, replacePrivateFileUrls, canonicalPrivateFileUrls } from './privateFiles';
// src/lib/api.js
import axios from "axios";
import { normalizeInquiryTerms } from "./notificationLabels";
import {
  isAuthApiError,
  isAuthRetryableFetchError,
  isAuthSessionMissingError,
} from "@supabase/supabase-js";
import { supabase } from "./supabase";

export const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export const AUTH_CHANGED_EVENT = "auth:changed";
export const AUTH_FORCE_LOGOUT_EVENT = "auth:force-logout";

// Wird im ActiveLocationContext + Interceptor verwendet
export const ACTIVE_LOCATION_STORAGE_KEY = "active_location_id";

const AUTH_TOKEN_KEY = "token"; // WICHTIG: Muss mit AuthContext übereinstimmen!
const AUTH_USER_KEY = "user";
const VISIT_AUTH_TOKEN_KEY = "admin_visit_token";
const VISIT_AUTH_USER_KEY = "admin_visit_user";
const VISIT_AUTH_RETURN_URL_KEY = "admin_visit_return_url";

function getSessionStorageItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function setSessionStorageItem(key, value) {
  try {
    if (value == null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function removeSessionStorageItem(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getVisitAuthToken() {
  return getSessionStorageItem(VISIT_AUTH_TOKEN_KEY);
}

export function getVisitAuthUser() {
  try {
    const raw = getSessionStorageItem(VISIT_AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasVisitAuthSession() {
  return Boolean(getVisitAuthToken());
}

export function setVisitAuthSession({ token, user } = {}) {
  if (token) {
    if (token !== getVisitAuthToken()) removeSessionStorageItem(VISIT_AUTH_USER_KEY);
    setSessionStorageItem(VISIT_AUTH_TOKEN_KEY, token);
  }
  if (user) setSessionStorageItem(VISIT_AUTH_USER_KEY, JSON.stringify(user));
  try {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch {
    // ignore
  }
}

export function getVisitAuthReturnUrl() {
  return getSessionStorageItem(VISIT_AUTH_RETURN_URL_KEY);
}

export function setVisitAuthReturnUrl(url) {
  if (!url) removeSessionStorageItem(VISIT_AUTH_RETURN_URL_KEY);
  else setSessionStorageItem(VISIT_AUTH_RETURN_URL_KEY, url);
}

export function setVisitAuthUser(user) {
  if (!user) {
    removeSessionStorageItem(VISIT_AUTH_USER_KEY);
  } else {
    setSessionStorageItem(VISIT_AUTH_USER_KEY, JSON.stringify(user));
  }
}

export function clearVisitAuthSession() {
  removeSessionStorageItem(VISIT_AUTH_TOKEN_KEY);
  removeSessionStorageItem(VISIT_AUTH_USER_KEY);
  removeSessionStorageItem(VISIT_AUTH_RETURN_URL_KEY);
  try {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch {
    // ignore
  }
}

export function getActiveLocationStorageKey() {
  const visitUser = getVisitAuthUser();
  const visitOrgId = String(visitUser?.org_id || "").trim();
  if (visitOrgId) return `${ACTIVE_LOCATION_STORAGE_KEY}:visit:${visitOrgId}`;
  return ACTIVE_LOCATION_STORAGE_KEY;
}

// -------------------------
// Token Helpers
// -------------------------
export function getAuthToken() {
  try {
    const visitToken = getSessionStorageItem(VISIT_AUTH_TOKEN_KEY);
    if (visitToken) return visitToken;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function clearApiCache() {
  cacheStore.clear();
  inflightStore.clear();
  activeCacheViews.clear();
  cacheGeneration += 1;
}

export function dispatchForceLogout(reason = "") {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_FORCE_LOGOUT_EVENT, { detail: { reason } }));
  } catch {
    // ignore
  }
}

function getActiveLocationId() {
  try {
    const value = localStorage.getItem(getActiveLocationStorageKey());
    if (!value || value === "all") return null;
    return value;
  } catch {
    return null;
  }
}

function getAuthCacheScope() {
  try {
    const visitUser = getVisitAuthUser();
    if (visitUser) {
      const visitUserId = String(visitUser?.id || "").trim();
      if (visitUserId) return `visit-user:${visitUserId}:org:${visitUser.org_id || ""}`;
      const visitEmail = String(visitUser?.email || "").trim().toLowerCase();
      if (visitEmail) return `visit-email:${visitEmail}:org:${visitUser.org_id || ""}`;
    }
  } catch {
    // ignore
  }

  // A visit waiting for its profile must never use the regular account cache.
  const pendingVisitToken = getVisitAuthToken();
  if (pendingVisitToken) return `visit-token:${pendingVisitToken}`;

  try {
    const rawUser = localStorage.getItem(AUTH_USER_KEY);
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      const userId = String(parsed?.id || "").trim();
      if (userId) return `user:${userId}:org:${parsed.org_id || ""}`;
      const email = String(parsed?.email || "").trim().toLowerCase();
      if (email) return `email:${email}:org:${parsed.org_id || ""}`;
    }
  } catch {
    // ignore
  }

  try {
    const token = getSessionStorageItem(VISIT_AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) return `token:${token}`;
  } catch {
    // ignore
  }

  return "anon";
}

// -------------------------
// Axios Instance
// -------------------------
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

const TECHNICAL_ERROR_TRANSLATIONS = [
  // Auth-Meldungen zuerst: Sie sind konkreter als die allgemeinen Statuscode-Regeln
  // weiter unten ("401 -> Bitte melde dich erneut an") und muessen diese schlagen.
  { match: /invalid login credentials|invalid credentials|invalid email or password|wrong password/i, text: "E-Mail oder Passwort ist falsch." },
  { match: /email not confirmed|email not verified/i, text: "Bitte bestätige zuerst deine E-Mail-Adresse." },
  { match: /already registered|already been registered|user already exists/i, text: "Diese E-Mail-Adresse ist bereits registriert." },
  { match: /user not found|no user found/i, text: "Es gibt kein Konto mit dieser E-Mail-Adresse." },
  { match: /password should be at least|password is too short/i, text: "Das Passwort ist zu kurz." },
  { match: /new password should be different/i, text: "Das neue Passwort muss sich vom bisherigen unterscheiden." },
  { match: /rate limit|too many requests|request failed with status code 429/i, text: "Zu viele Versuche. Bitte warte einen Moment und versuche es erneut." },
  { match: /jwt expired|token (has )?expired|session (expired|not found)|invalid refresh token|refresh token not found/i, text: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an." },
  { match: /signups? (not allowed|disabled)/i, text: "Registrierungen sind derzeit nicht möglich." },
  { match: /unable to validate email|email address .{0,40}invalid/i, text: "Die E-Mail-Adresse ist ungültig." },
  { match: /network error|failed to fetch|load failed/i, text: "Verbindung zum Server fehlgeschlagen. Bitte prüfe deine Internetverbindung." },
  { match: /request failed with status code 400/i, text: "Die Eingabe konnte nicht verarbeitet werden. Bitte prüfe die Angaben." },
  { match: /request failed with status code 401|unauthorized|not authenticated/i, text: "Bitte melde dich erneut an." },
  { match: /request failed with status code 403|forbidden/i, text: "Du hast dafür keine Berechtigung." },
  { match: /request failed with status code 404|not found/i, text: "Der Eintrag wurde nicht gefunden." },
  { match: /request failed with status code 409|duplicate/i, text: "Dieser Eintrag existiert bereits oder steht im Konflikt mit vorhandenen Daten." },
  { match: /request failed with status code 422|validation error|input should/i, text: "Bitte prüfe die Eingaben." },
  { match: /request failed with status code 5\d\d|internal server error|server error/i, text: "Auf dem Server ist ein Fehler aufgetreten. Bitte versuche es erneut." },
  { match: /missing[_\s-]?email/i, text: "E-Mail-Adresse fehlt." },
  { match: /missing[_\s-]?user/i, text: "Benutzerdaten fehlen." },
  { match: /missing[_\s-]?resend[_\s-]?api[_\s-]?key/i, text: "E-Mail-Versand ist noch nicht konfiguriert." },
  { match: /custom[_\s-]?mail[_\s-]?failed|send[_\s-]?failed/i, text: "E-Mail konnte nicht gesendet werden." },
  { match: /token[_\s-]?insert[_\s-]?failed/i, text: "Sicherheitslink konnte nicht erstellt werden." },
  { match: /auth[_\s-]?user[_\s-]?not[_\s-]?found/i, text: "Benutzerkonto wurde nicht gefunden." },
  { match: /reset[_\s-]?failed/i, text: "Passwort-Reset fehlgeschlagen." },
  { match: /invalid/i, text: "Ungültige Eingabe." },
  { match: /required/i, text: "Bitte fülle alle Pflichtfelder aus." },
];

// Haeufige deutsche Woerter, die in englischen Fehlermeldungen praktisch nie
// vorkommen. Bewusst breit: Die alte, kurze Liste erkannte Backend-Texte wie
// "E-Mail oder Passwort ist falsch." nicht als deutsch (kein Umlaut, kein
// Stichwort), wodurch sie im generischen Fallback verschwanden.
const GERMAN_MESSAGE_WORDS = /\b(ist|sind|wurde|wurden|haben|nicht|kein|keine|keinen|bitte|oder|und|noch|schon|bereits|falsch|fehlt|fehlen|fehlgeschlagen|ungültig|ungueltig|abgelaufen|erneut|versuch\w*|konnte|kann|können|muss|müssen|darf|dürfen|erforderlich|gefunden|vorhanden|existiert|berechtigung|gelöscht|gespeichert|passwort|anmeldung|angemeldet|konto|sitzung|zugriff|eingabe|eingaben|benutzer|prüfe\w*|pruefe\w*|warte\w*|viele|kurz|dich|deine|melde)\b/i;

function isLikelyGerman(text) {
  const value = String(text || "");
  return /[äöüÄÖÜß]/.test(value) || GERMAN_MESSAGE_WORDS.test(value);
}

// Detect raw technical leakage (JSON dumps, Postgres error codes, stack traces)
// that must never be shown to end users.
function looksLikeTechnicalDump(text) {
  const value = String(text || "");
  return /[{}\[\]]/.test(value)              // JSON/dict braces
    || /'\s*message\s*'\s*:/i.test(value)    // {'message': ...}
    || /"\s*message\s*"\s*:/i.test(value)    // {"message": ...}
    || /\bcode'?\s*:\s*'?\d/i.test(value)    // 'code': '22P02'
    || /\b\d{2}[A-Z]\d{2}\b/.test(value)     // Postgres SQLSTATE e.g. 22P02
    || /Traceback|Exception|psycopg|asyncpg|supabase/i.test(value);
}

function translateTechnicalMessage(message, fallback = "Ein Fehler ist aufgetreten. Bitte versuche es erneut.") {
  const raw = humanizeValidationText(message).trim();
  if (!raw) return fallback;
  // Never surface raw JSON / DB error dumps, even if they contain German words.
  if (looksLikeTechnicalDump(raw)) {
    const hit = TECHNICAL_ERROR_TRANSLATIONS.find((item) => item.match.test(raw));
    return hit?.text || fallback;
  }
  if (isLikelyGerman(raw)) return raw;
  const hit = TECHNICAL_ERROR_TRANSLATIONS.find((item) => item.match.test(raw));
  return hit?.text || fallback;
}

export function getUserErrorMessage(error, fallback = "Ein Fehler ist aufgetreten. Bitte versuche es erneut.") {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        const field = item?.loc?.at(-1) || item?.field;
        if (field === 'email') return 'Bitte eine gültige E-Mail-Adresse eingeben.';
        const message = translateTechnicalMessage(item?.msg || item?.message || item, 'Bitte diese Angabe prüfen.');
        return field ? `${validationFieldLabel(field)}: ${message}` : message;
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  if (detail && typeof detail === "object") {
    return translateTechnicalMessage(detail.message || detail.error || detail.code, fallback);
  }
  if (detail) return translateTechnicalMessage(detail, fallback);
  return translateTechnicalMessage(error?.message, fallback);
}

// Prüft beim Server, ob die Supabase-Session noch existiert.
//
// getSession() reicht dafür nicht: Es liest nur den lokalen Cache und meldet ein
// serverseitig längst widerrufenes Token weiterhin als gültig. Nur getUser() fragt
// Supabase wirklich.
//
// Die Unterscheidung ist essenziell, weil das Backend JEDEN Auth-Provider-Fehler in
// ein 401 übersetzt, auch Timeouts. Würden wir auf jedes 401 hin ausloggen, würfe ein
// kurzer Supabase-Ausfall sämtliche Nutzer heraus. Deshalb gilt nur ein echtes
// Ablehnen des Tokens (401/403) als tote Session; 5xx und Netzfehler nicht.
let deadSessionProbe = null;

export function sessionIsDead() {
  if (deadSessionProbe) return deadSessionProbe;

  const probe = (async () => {
    const checkedToken = getAuthToken();
    try {
      const { error } = await supabase.auth.getUser();
      // A delayed rejection for an older token must not log out a session
      // that has meanwhile refreshed or switched to another account.
      if (getAuthToken() !== checkedToken) return false;
      if (!error) return false;
      if (isAuthRetryableFetchError(error)) return false;
      if (isAuthSessionMissingError(error)) return true;
      return isAuthApiError(error) && (error.status === 401 || error.status === 403);
    } catch {
      return false;
    }
  })();

  deadSessionProbe = probe;
  probe.finally(() => {
    if (deadSessionProbe === probe) deadSessionProbe = null;
  });
  return probe;
}

async function getPreferredAccessToken() {
  const visitToken = getVisitAuthToken();
  if (visitToken) return visitToken;
  try {
    const { data } = await supabase.auth.getSession();
    const access = data?.session?.access_token || null;
    if (access) {
      setAuthToken(access);
      return access;
    }
  } catch {
    // ignore
  }
  return getAuthToken();
}

// Request Interceptor: Auth + Location Header
api.interceptors.request.use(async (config) => {
  if (!String(config.url || "").startsWith("/private-files/")) config.data = canonicalPrivateFileUrls(config.data);
  // The instance defaults to JSON. Multipart endpoints need the real file
  // part; otherwise FastAPI reports "body.file: Dieses Feld ist erforderlich".
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers = config.headers || {};
    config.headers['Content-Type'] = 'multipart/form-data';
  }
  const token = await getPreferredAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Nur für ADMIN-Requests: X-Location-Id mitsenden (server.py unterstützt Query ODER Header)
  const url = config.url || "";
  const isPublic = url.startsWith("/public/");
  const isAuth = url.startsWith("/auth/");
  const isConfig = url === "/config" || url === "/health";

  if (!isPublic && !isAuth && !isConfig) {
    const locId = getActiveLocationId();
    if (locId) {
      config.headers = config.headers || {};
      config.headers["X-Location-Id"] = locId;
    }
  }

  return config;
});

// Response Interceptor: 401 -> Token löschen (optional)
api.interceptors.response.use(
  async (res) => {
    if (['post', 'put', 'patch', 'delete'].includes(res.config?.method) && res.config?.url !== '/private-files/resolve') {
      invalidateViewCache();
    }
    if (!String(res.config?.url || '').startsWith('/private-files/') && !String(res.config?.url || '').startsWith('/public/')) {
      const urls = privateFileUrls(res.data);
      const resolved = {};
      try {
        for (let offset = 0; offset < urls.length; offset += 100) {
          Object.assign(resolved, (await api.post('/private-files/resolve', { urls: urls.slice(offset, offset + 100) })).data);
        }
      } catch (error) {
        // The write/upload has already succeeded. A temporary signing outage
        // must not invite a retry that creates duplicate records or uploads.
        // Reads still report the error so the user can reload the file links.
        if (!['post', 'put', 'patch', 'delete'].includes(res.config?.method)) throw error;
        console.warn('Datei gespeichert; Vorschau-Link konnte nicht geladen werden. Bitte erneut öffnen.');
      }
      if (urls.length) res.data = replacePrivateFileUrls(res.data, resolved);
    }
    return res;
  },
  async (err) => {
    // PDF requests also receive JSON validation errors as blobs.
    // Decode these before translating, so the server's concrete reason survives.
    const responseData = err?.response?.data;
    if (typeof Blob !== "undefined" && responseData instanceof Blob) {
      try {
        const body = await responseData.text();
        err.response.data = JSON.parse(body);
      } catch {
        // Non-JSON error responses keep the usual status-code fallback.
      }
    }
    const userMessage = normalizeInquiryTerms(getUserErrorMessage(err));
    err.userMessage = userMessage;
    err.message = userMessage;
    if (err?.response?.data && err.response.data.detail) {
      const responseDetail = err.response.data.detail;
      err.response.data.detail = responseDetail && typeof responseDetail === "object" && !Array.isArray(responseDetail)
        ? { ...responseDetail, message: userMessage }
        : userMessage;
    }
    if (err?.response?.status === 401) {
      const url = String(err?.config?.url || "");
      const isInteractiveAuthRequest =
        url.startsWith("/auth/login") ||
        url.startsWith("/auth/register") ||
        url.startsWith("/auth/register-with-trial") ||
        url.startsWith("/auth/forgot-password") ||
        url.startsWith("/auth/me") ||
        url.startsWith("/admin/me");

      // Vorher wurde nur ausgeloggt, wenn GAR KEIN Token mehr da war. Ein Token, das
      // zwar existiert, vom Server aber abgelehnt wird, blieb dadurch für immer liegen:
      // Jeder Request lief in ein 401, ohne dass die Session je geräumt wurde.
      //
      // Besuchs-Sessions tragen ein Backend-Token, das Supabase nicht kennt und daher
      // nicht prüfbar ist - die bleiben wie bisher unangetastet.
      if (!isInteractiveAuthRequest && !hasVisitAuthSession() && (await sessionIsDead())) {
        clearAuthToken();
        dispatchForceLogout(userMessage || "Sitzung nicht mehr gültig");
      }
    }
    return Promise.reject(err);
  }
);

// -------------------------
// Small helper for abort signals
// -------------------------
function withSignal(signal) {
  return signal ? { signal } : undefined;
}

// -------------------------
// Simple in-memory cache for list endpoints
// -------------------------
const CACHE_TTL_MS = 60000;
const cacheStore = new Map();
const inflightStore = new Map();
const activeCacheViews = new Set();
let cacheGeneration = 0;
if (typeof window !== "undefined") {
  window.addEventListener(AUTH_CHANGED_EVENT, clearApiCache);
  window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, clearApiCache);
  window.addEventListener("storage", clearApiCache);
}

function buildCacheKey(url, params, varyByLocation = true) {
  const loc = varyByLocation ? (getActiveLocationId() || "all") : "global";
  const authScope = getAuthCacheScope();
  const paramKey = params ? JSON.stringify(Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined).sort(([a], [b]) => a.localeCompare(b)))) : "";
  return `${url}|${authScope}|${loc}|${paramKey}`;
}

function isCacheViewActive(key) {
  return [...activeCacheViews].some(view => view.matches(key));
}

// Keep a visible report's snapshots; start the grace period when it is left.
export function retainReportView(prefixes) {
  const authScope = `|${getAuthCacheScope()}|`;
  const scopes = [authScope + (getActiveLocationId() || 'all') + '|', authScope + 'global|'];
  const matches = key => prefixes.some(prefix => key.startsWith(prefix + '|')) && scopes.some(scope => key.includes(scope));
  // Expired snapshots must not be revived by returning to a view.
  for (const [key, entry] of cacheStore) {
    if (matches(key) && !isCacheViewActive(key) && Date.now() - entry.timestamp > CACHE_TTL_MS) cacheStore.delete(key);
  }
  const view = { matches };
  activeCacheViews.add(view);
  return () => {
    if (!activeCacheViews.delete(view)) return;
    for (const [key, entry] of cacheStore) {
      if (matches(key) && !isCacheViewActive(key)) entry.timestamp = Date.now();
    }
  };
}

function cacheGet(key) {
  const entry = cacheStore.get(key);
  if (!entry) return null;
  if (!isCacheViewActive(key) && Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cacheStore.delete(key);
    return null;
  }
  return entry.data;
}

function cacheSet(key, data) {
  if (cacheStore.size >= 256 && !cacheStore.has(key)) cacheStore.delete(cacheStore.keys().next().value);
  cacheStore.set(key, { timestamp: Date.now(), data });
}

function cacheInvalidate(prefix) {
  cacheGeneration += 1;
  for (const key of inflightStore.keys()) {
    if (key.startsWith(prefix)) inflightStore.delete(key);
  }
  for (const key of cacheStore.keys()) {
    if (key.startsWith(prefix)) cacheStore.delete(key);
  }
}

// Financial/report snapshots are short lived and invalidated after successful writes.
export function invalidateReportCache() {
  ['/reports/', '/payments/outstanding-fees', '/goae/invoices', '/patient-acquisition/overview',
    '/patient-acquisition/opportunities', '/patient-acquisition/campaigns'].forEach(cacheInvalidate);
}

function consumeCachedRequest(request, signal) {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(new axios.CanceledError());
  return new Promise((resolve, reject) => {
    const abort = () => { signal.removeEventListener('abort', abort); reject(new axios.CanceledError()); };
    signal.addEventListener('abort', abort, { once: true });
    request.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

async function cachedGet(url, params, config, options = {}) {
  const { varyByLocation = true, cacheUrl = url, selectData } = options;
  const { signal, forceRefresh = false, ...requestConfig } = config || {};
  if (signal?.aborted) throw new axios.CanceledError();
  const effectiveParams = requestConfig.params || params;
  const key = buildCacheKey(cacheUrl, effectiveParams, varyByLocation)
    + (requestConfig.headers ? '|headers:' + JSON.stringify(requestConfig.headers) : '');
  if (forceRefresh) { cacheStore.delete(key); inflightStore.delete(key); }
  const cached = cacheGet(key);
  if (cached !== null) return { data: cached, status: 200, cached: true };
  if (inflightStore.has(key)) return consumeCachedRequest(inflightStore.get(key), signal);
  const generation = cacheGeneration;
  // Each consumer can cancel independently. The shared read may finish and warm
  // the bounded in-memory cache; a navigation must not cancel another view.
  const req = api.get(url, { params: effectiveParams, ...requestConfig })
    .then((res) => {
      if (selectData) res = { ...res, data: selectData(res.data) };
      if (generation === cacheGeneration && inflightStore.get(key) === req) cacheSet(key, res.data);
      if (inflightStore.get(key) === req) inflightStore.delete(key);
      return res;
    }).catch((err) => {
      if (inflightStore.get(key) === req) inflightStore.delete(key);
      throw err;
    });
  inflightStore.set(key, req);
  return consumeCachedRequest(req, signal);
}

const VIEW_LIST_ENDPOINTS = ['/organization/display-settings', '/clients', '/clients/summary', '/forms', '/policies',
  '/face-map-materials', '/material-batches', '/marketing/campaigns', '/marketing/templates',
  '/marketing/automations', '/instagram/automations', '/goae/practitioners',
  '/estimates', '/goae/templates', '/goae/treatment-templates', '/goae/catalog', '/dashboard/metrics'];

export function invalidateViewCache() {
  invalidateReportCache();
  VIEW_LIST_ENDPOINTS.forEach(cacheInvalidate);
}

// -------------------------
// AUTH (server.py: /auth/*)
// -------------------------
export const authAPI = {
  login: (payload, config) => api.post("/auth/login", payload, config),
  register: (payload, config) => api.post("/auth/register", payload, config),
  registerWithTrial: (payload, config) => api.post("/auth/register-with-trial", payload, config),
  verifyEmail: (payload, config) => api.post("/auth/verify-email", payload, config),
  resendVerification: (config) => api.post("/auth/email-verification/resend", {}, config),
  forgotPassword: (payload, config) => api.post("/auth/forgot-password", payload, config),
  resetPasswordConfirm: (payload, config) => api.post("/auth/password-reset/confirm", payload, config),
  completeOnboarding: (payload, config) => api.post("/auth/onboarding-complete", payload || {}, config),
  me: (config) => api.get("/auth/me", config),
};

// -------------------------
// SETUP STATUS (server.py: /setup/*)
// -------------------------
export const setupAPI = {
  getStatus: (config) => api.get("/setup/status", config),
  updateStatus: (payload, config) => api.put("/setup/status", payload, config),
  check: (config) => api.post("/setup/check", null, config),
};

// -------------------------
// ORG (server.py: /organization)
// -------------------------
export const orgAPI = {
  // Only display metadata is retained; organization/security settings stay fresh.
  getDisplaySettings: (config) => cachedGet("/organization", { compact: true }, config, {
    varyByLocation: false, cacheUrl: "/organization/display-settings",
    selectData: data => ({ currency: String(data?.currency || "EUR").toUpperCase() }),
  }),
  get: (config) => api.get("/organization", config),
  update: (payload, config) => api.put("/organization", payload, config),
  acceptAvv: (config) => api.post("/organization/avv/accept", {}, config),
  uploadLogo: (file, config) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/organization/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
};

// -------------------------
// SPRACHNOTIZ (server.py: /transcription)
// -------------------------
export const transcriptionAPI = {
  // Ohne hinterlegten Schlüssel gibt es keine Spracheingabe. Die Oberfläche
  // fragt das einmal ab und blendet den Aufnahmeknopf sonst aus.
  status: (config) => api.get("/transcription/status", config),
  transcribe: (blob, context, config) => {
    const formData = new FormData();
    // Der Dateiname trägt das Format – daran erkennt der Anbieter die Aufnahme.
    const extension = String(blob?.type || "").includes("mp4") ? "mp4" : "webm";
    formData.append("file", blob, `aufnahme.${extension}`);
    formData.append("context", context || "");
    return api.post("/transcription/audio", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
};

// -------------------------
// USER PROFILE (server.py: /users/me)
// -------------------------
export const userAPI = {
  updateMe: (payload, config) => api.put("/users/me", payload, config),
  changeEmail: (payload, config) => api.post("/users/me/change-email", payload, config),
};

// -------------------------
// SUBSCRIPTION (server.py: /subscription)
// -------------------------
export const subscriptionAPI = {
  get: (config) => cachedGet("/subscription", undefined, config, { varyByLocation: false }),
  getFresh: (config) => api.get("/subscription", config).then((res) => {
    cacheSet(buildCacheKey("/subscription", undefined, false), res.data);
    return res;
  }),
  updateAddons: (payload, config) =>
    api.patch("/subscription/addons", payload, config).then((res) => {
      cacheInvalidate("/subscription");
      return res;
    }),
};

// -------------------------
// ADD-ONS (server.py: /addons)
// -------------------------
export const addonsAPI = {
  list: (config) => api.get("/addons", config),
  listCatalog: (config) => api.get("/addons/catalog", config),
};

// -------------------------
// LOCATIONS (server.py: /locations)
// -------------------------
export const locationsAPI = {
  list: (config) => cachedGet("/locations", undefined, config, { varyByLocation: false }),
  create: (payload, config) =>
    api.post("/locations", payload, config).then((res) => {
      cacheInvalidate("/locations");
      cacheInvalidate("/subscription");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/locations/${id}`, payload, config).then((res) => {
      cacheInvalidate("/locations");
      return res;
    }),
  duplicate: (id, config) =>
    api.post(`/locations/${id}/duplicate`, {}, config).then((res) => {
      cacheInvalidate("/locations");
      cacheInvalidate("/subscription");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/locations/${id}`, config).then((res) => {
      cacheInvalidate("/locations");
      cacheInvalidate("/subscription");
      return res;
    }),
  uploadImage: (formData, config) =>
    api.post("/locations/upload-image", formData, {
      ...(config || {}),
      headers: {
        ...((config || {}).headers || {}),
        "Content-Type": "multipart/form-data",
      },
    }),
  deleteImage: (imageUrl, config) =>
    api.delete("/locations/image", { ...(config || {}), params: { image_url: imageUrl } }),
  listGallery: (locationId, config) =>
    api.get(`/locations/${locationId}/gallery`, config),
  createGalleryImage: (locationId, payload, config) =>
    api.post(`/locations/${locationId}/gallery`, payload, config),
  reorderGalleryImages: (locationId, imageIds, config) =>
    api.put(`/locations/${locationId}/gallery/order`, { image_ids: imageIds }, config),
  deleteGalleryImage: (locationId, imageId, config) =>
    api.delete(`/locations/${locationId}/gallery/${imageId}`, config),
  listAbsences: (locationId, config) =>
    api.get(`/locations/${locationId}/absences`, config),
  createAbsence: (locationId, payload, config) =>
    api.post(`/locations/${locationId}/absences`, payload, config),
  deleteAbsence: (locationId, absenceId, config) =>
    api.delete(`/locations/${locationId}/absences/${absenceId}`, config),
  setupCustomDomain: (id, payload, config) =>
    api.post(`/locations/${id}/custom-domain`, payload, config).then((res) => {
      cacheInvalidate("/locations");
      return res;
    }),
  verifyCustomDomain: (id, config) =>
    api.post(`/locations/${id}/custom-domain/verify`, {}, config).then((res) => {
      cacheInvalidate("/locations");
      return res;
    }),
  // Opening Hours
  getOpeningHours: (locationId, config) =>
    api.get(`/locations/${locationId}/opening-hours`, config),
  // Tage, an denen der Standort ausnahmsweise geöffnet wurde (aus dem Schichtplan)
  getOpenedDates: (locationId, params, config) =>
    api.get(`/locations/${locationId}/opened-dates`, { params, ...(config || {}) }),
  createOpeningHour: (locationId, payload, config) =>
    api.post(`/locations/${locationId}/opening-hours`, payload, config),
  deleteOpeningHour: (locationId, weekday, config) =>
    api.delete(`/locations/${locationId}/opening-hours/${weekday}`, config),
  bulkUpdateOpeningHours: (locationId, hours, config) =>
    api.post(`/locations/${locationId}/opening-hours/bulk`, hours, config),
  searchGooglePlaces: (query, config) =>
    api.get(`/google-places/search`, { params: { q: query }, ...(config || {}) }),
};

// -------------------------
// CLIENTS (server.py: /clients)
// -------------------------
export const clientsAPI = {
  list: (params, config) => (params?.compact && !params?.lookup) ? cachedGet("/clients", params, config) : api.get("/clients", { params, ...(config || {}) }),
  summary: (params, config) => cachedGet("/clients/summary", params, config),
  get: (id, config) => api.get(`/clients/${id}`, config),
  prescriptionTemplate: (id, config) => api.get(`/clients/${id}/prescription-template`, { responseType: 'blob', ...(config || {}) }),
  createPrescription: (id, payload, config) => api.post(`/clients/${id}/prescriptions`, payload, config),
  previewPrescription: (id, payload, config) => api.post(`/clients/${id}/prescriptions/preview`, payload, { responseType: 'blob', ...(config || {}) }),
  createAttestation: (id, payload, config) => api.post(`/clients/${id}/attestations`, payload, config),
  previewAttestation: (id, payload, config) => api.post(`/clients/${id}/attestations/preview`, payload, { responseType: 'blob', ...(config || {}) }),
  createWorkIncapacity: (id, payload, config) => api.post(`/clients/${id}/work-incapacity`, payload, config),
  previewWorkIncapacity: (id, payload, config) => api.post(`/clients/${id}/work-incapacity/preview`, payload, { responseType: 'blob', ...(config || {}) }),
  create: (payload, config) => api.post("/clients", payload, config),
  update: (id, payload, config) => api.put(`/clients/${id}`, payload, config),
  delete: (id, config) => api.delete(`/clients/${id}`, config),
  bulkDelete: (ids, config) => api.post("/clients/bulk-delete", { ids }, config),
  bulkTag: (ids, tag, config) => api.post("/clients/bulk-tag", { ids, tag }, config),
  listMedia: (id, config) => api.get(`/clients/${id}/media`, config),
  uploadMedia: (id, file, kind = "document", config) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("target", "client");
    formData.append("target_id", id);
    formData.append("kind", kind);
    formData.append("scope", "client");
    formData.append("entity_id", id);
    formData.append("file_type", kind);
    return api.post("/mobile/uploads", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
  listFaceMaps: (id, config) => api.get(`/clients/${id}/face-maps`, config),
  createFaceMap: (id, payload, config) => api.post(`/clients/${id}/face-maps`, payload, config),
  updateFaceMap: (id, mapId, payload, config) => api.patch(`/clients/${id}/face-maps/${mapId}`, payload, config),
  deleteFaceMap: (id, mapId, config) => api.delete(`/clients/${id}/face-maps/${mapId}`, config),
  duplicateFaceMap: (id, mapId, config) => api.post(`/clients/${id}/face-maps/${mapId}/duplicate`, {}, config),
  reorderFaceMaps: (id, payload, config) => api.post(`/clients/${id}/face-maps/reorder`, payload, config),
  importPreview: (formData, config) =>
    api.post("/clients/import/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    }),
  importCommit: (formData, config) =>
    api.post("/clients/import/commit", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    }),
  createDataCheckLink: (payload = {}) => api.post("/clients/data-check-links", payload),
  listDataCheckLinks: () => api.get("/clients/data-check-links"),
  revokeDataCheckLink: (id) => api.post(`/clients/data-check-links/${id}/revoke`, {}),
  listDataChecks: () => api.get("/clients/data-check-submissions"),
  countDataChecks: (config) => api.get("/clients/data-check-submissions/count", config),
  approveDataCheck: (id, payload) => api.post(`/clients/data-check-submissions/${id}/approve`, payload),
  rejectDataCheck: (id) => api.post(`/clients/data-check-submissions/${id}/reject`, {}),
  invalidateList: () => cacheInvalidate("/clients"),
  listFormSubmissions: (id, config) => api.get(`/clients/${id}/form-submissions`, config),
  listShopOrders: (id, config) => api.get(`/clients/${id}/shop-orders`, config),
  listActivity: (id, params, config) => api.get(`/clients/${id}/activity`, { params, ...(config || {}) }),
};

export const publicDataCheckAPI = {
  get: (token) => api.get(`/public/data-check/${encodeURIComponent(token)}`),
  submit: (token, payload) => api.post(`/public/data-check/${encodeURIComponent(token)}`, payload),
};

export const clientNotesAPI = {
  list: (clientId, config) => api.get(`/clients/${clientId}/notes`, config),
  create: (clientId, payload, config) => api.post(`/clients/${clientId}/notes`, payload, config),
  delete: (clientId, noteId, config) => api.delete(`/clients/${clientId}/notes/${noteId}`, config),
};

export const giftCardsAPI = {
  list: (params, config) => cachedGet("/gift-cards", params, config),
  create: (payload, config) =>
    api.post("/gift-cards", payload, config).then((res) => {
      cacheInvalidate("/gift-cards");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/gift-cards/${id}`, payload, config).then((res) => {
      cacheInvalidate("/gift-cards");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/gift-cards/${id}`, config).then((res) => {
      cacheInvalidate("/gift-cards");
      return res;
    }),
  redeem: (id, payload, config) =>
    api.post(`/gift-cards/${id}/redeem`, payload, config).then((res) => {
      cacheInvalidate("/gift-cards");
      return res;
    }),
  redemptions: (id, config) => api.get(`/gift-cards/${id}/redemptions`, config),
  validatePublic: (payload) => api.post("/public/voucher/validate", payload),
};

export const productsAPI = {
  list: (params, config) => cachedGet("/products", params, config),
  create: (payload, config) =>
    api.post("/products", payload, config).then((res) => {
      cacheInvalidate("/products");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/products/${id}`, payload, config).then((res) => {
      cacheInvalidate("/products");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/products/${id}`, config).then((res) => {
      cacheInvalidate("/products");
      return res;
    }),
};

// -------------------------
// SERVICES (server.py: /services)
// list supports location_id query OR X-Location-Id header
// -------------------------
export const servicesAPI = {
  list: (params, config) => cachedGet("/services", params, config),
  create: (payload, config) =>
    api.post("/services", payload, config).then((res) => {
      cacheInvalidate("/services");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/services/${id}`, payload, config).then((res) => {
      cacheInvalidate("/services");
      return res;
    }),
  duplicate: (id, config) =>
    api.post(`/services/${id}/duplicate`, {}, config).then((res) => {
      cacheInvalidate("/services");
      cacheInvalidate("/service-categories");
      return res;
    }),
  delete: (id, params, config) =>
    api.delete(`/services/${id}`, { ...(config || {}), params }).then((res) => {
      cacheInvalidate("/services");
      return res;
    }),
  uploadImage: (formData, config) =>
    api.post("/services/upload-image", formData, {
      ...(config || {}),
      headers: {
        ...((config || {}).headers || {}),
        "Content-Type": "multipart/form-data",
      },
    }),
  deleteImage: (imageUrl, config) =>
    api.delete('/services/image', { ...(config || {}), params: { image_url: imageUrl } }),
  getStaff: (serviceId, config) => api.get(`/services/${serviceId}/staff`, config),
  setStaff: (serviceId, payload, config) => {
    const body = Array.isArray(payload) ? { staff_ids: payload } : payload;
    return api.put(`/services/${serviceId}/staff`, body, config).then((res) => {
      cacheInvalidate("/services");
      cacheInvalidate("/staff");
      return res;
    });
  },
};

// -------------------------
// SERVICE CATEGORIES (server.py: /service-categories)
// list supports location_id query OR X-Location-Id header
// -------------------------
export const serviceCategoriesAPI = {
  list: (params, config) => cachedGet("/service-categories", params, config),
  create: (payload, config) =>
    api.post("/service-categories", payload, config).then((res) => {
      cacheInvalidate("/service-categories");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/service-categories/${id}`, payload, config).then((res) => {
      cacheInvalidate("/service-categories");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/service-categories/${id}`, config).then((res) => {
      cacheInvalidate("/service-categories");
      return res;
    }),
};

// -------------------------
// POLICIES (server.py: /policies)
// -------------------------
export const policiesAPI = {
  list: (config) => cachedGet("/policies", undefined, config),
  create: (payload, config) => api.post("/policies", payload, config),
  update: (id, payload, config) => api.put(`/policies/${id}`, payload, config),
  delete: (id, config) => api.delete(`/policies/${id}`, config),
};

// -------------------------
// STAFF (server.py: /staff)
// list supports location_id query OR X-Location-Id header
// -------------------------
export const staffAPI = {
  list: (params, config) => cachedGet("/staff", params, config),
  create: (payload, config) =>
    api.post("/staff", payload, config).then((res) => {
      cacheInvalidate("/staff");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/staff/${id}`, payload, config).then((res) => {
      cacheInvalidate("/staff");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/staff/${id}`, config).then((res) => {
      cacheInvalidate("/staff");
      return res;
    }),

  resendInvite: (staffId, config) =>
    api.post(`/staff/${staffId}/resend-invite`, {}, config).then((res) => {
      cacheInvalidate("/staff");
      return res;
    }),
  getServices: (staffId, config) =>
    api.get(`/staff/${staffId}/services`, config),
  setServices: (staffId, payload, config) => {
    const body = Array.isArray(payload) ? { service_ids: payload } : payload;
    return api.put(`/staff/${staffId}/services`, body, config).then((res) => {
      cacheInvalidate("/staff");
      cacheInvalidate("/services");
      return res;
    });
  },
};

// -------------------------
// AVAILABILITY RULES
// -------------------------
export const availabilityAPI = {
  list: (params, config) => cachedGet("/availability-rules", params, { ...(config || {}) }),
  create: (payload, config) =>
    api.post("/availability-rules", payload, config).then((res) => {
      cacheInvalidate("/availability-rules");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/availability-rules/${id}`, payload, config).then((res) => {
      cacheInvalidate("/availability-rules");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/availability-rules/${id}`, config).then((res) => {
      cacheInvalidate("/availability-rules");
      return res;
    }),
};

// -------------------------
// CENTRAL WORK SCHEDULE
// -------------------------
export const workScheduleAPI = {
  getWeek: (params, config) =>
    api.get('/work-schedule', { params, ...(config || {}) }),
  saveDay: (payload, config) =>
    api.put('/work-schedule/day', payload, config).then((res) => {
      cacheInvalidate('/work-schedule');
      cacheInvalidate('/availability-rules');
      return res;
    }),
  removeDay: (params, config) =>
    api.delete('/work-schedule/day', { params, ...(config || {}) }).then((res) => {
      cacheInvalidate('/work-schedule');
      return res;
    }),
  saveStandard: (payload, config) =>
    api.put('/work-schedule/standard', payload, config).then((res) => {
      cacheInvalidate('/work-schedule');
      cacheInvalidate('/availability-rules');
      return res;
    }),
};

// -------------------------
// ABSENCES
// -------------------------
export const absencesAPI = {
  list: (params, config) => cachedGet("/absences", params, { ...(config || {}) }),
  create: (payload, config) =>
    api.post("/absences", payload, config).then((res) => {
      cacheInvalidate("/absences");
      return res;
    }),
  update: (id, payload, config) =>
    api.put(`/absences/${id}`, payload, config).then((res) => {
      cacheInvalidate("/absences");
      return res;
    }),
  delete: (id, config) =>
    api.delete(`/absences/${id}`, config).then((res) => {
      cacheInvalidate("/absences");
      return res;
    }),
};

// -------------------------
// WAITLIST
// -------------------------
export const waitlistAPI = {
  list: (params, config) => api.get("/waitlist", { params, ...(config || {}) }),
  create: (payload, config) => api.post("/waitlist", payload, config),
  sendProposal: (id, payload, config) => api.post(`/waitlist/${id}/proposal`, payload, config),
  delete: (id, config) => api.delete(`/waitlist/${id}`, config),
};

// -------------------------
// APPOINTMENTS (server.py: /appointments)
// supports location_id query OR X-Location-Id header
// -------------------------
export const appointmentsAPI = {
  list: (params, config) =>
    api.get("/appointments", { params, ...(config || {}) }),
  search: (params, config) =>
    api.get("/appointments/search", { params, ...(config || {}) }),
  get: (id, config) => api.get(`/appointments/${id}`, config),
  create: (payload, config) => api.post("/appointments", payload, config),
  update: (id, payload, config) => api.patch(`/appointments/${id}`, payload, config),
  cancel: (id, payload, config) => api.post(`/appointments/${id}/cancel`, payload, config),
  cancelAndRefund: (id, payload, config) => api.post(`/appointments/${id}/cancel-and-refund`, payload, config),
  refund: (id, payload, config) => api.post(`/appointments/${id}/refund`, payload, config),
  confirm: (id, config) => api.post(`/appointments/${id}/confirm`, {}, config),
  decline: (id, config) => api.post(`/appointments/${id}/decline`, {}, config),
  noShow: (id, payload, config) => api.post(`/appointments/${id}/no-show`, payload, config),
  reschedule: (id, payload, config) => api.post(`/appointments/${id}/reschedule`, payload, config),
  sendPaymentReminder: (id, payload = {}, config) => api.post(`/appointments/${id}/payment-reminder`, payload, config),
  sendProposal: (id, payload, config) => api.post(`/appointments/${id}/proposal`, payload, config),
  createRecurring: (payload, config) => api.post("/appointments/recurring", payload, config),
  updateRecurring: (recurrenceId, payload, config) =>
    api.patch(`/appointments/recurring/${recurrenceId}`, payload, config),
  cancelRecurring: (recurrenceId, payload, config) =>
    api.post(`/appointments/recurring/${recurrenceId}/cancel`, payload, config),
  listFiles: (id, config) => api.get(`/appointments/${id}/files`, config),
  createFile: (id, payload, config) => api.post(`/appointments/${id}/files`, payload, config),
  deleteFile: (id, fileId, config) => api.delete(`/appointments/${id}/files/${fileId}`, config),
  // Terminergebnis (Behandlungsstatus)
  saveOutcome: (id, payload, config) => api.put(`/appointments/${id}/outcome`, payload, config),
  updateDecision: (id, payload, config) => api.patch(`/appointments/${id}/decision`, payload, config),
  pendingOutcomes: (params = {}, config) =>
    api.get("/appointments/outcomes/pending", { params, ...(config || {}) }),
};

export const goaeWorkflowAPI = {
  complete: (id, body) => api.post(`/goae/workflow/${id}/complete`, body),
  purchaseDefaults: () => api.get('/goae/purchase-defaults'),
  savePurchaseDefaults: body => api.put('/goae/purchase-defaults', body),
  confirm: (id, body) => api.post(`/goae/workflow/${id}/confirm`, body),
  status: () => api.get('/goae/workflow/status'),
  state: (id) => api.get(`/goae/workflow/${id}`),
  linkServiceTemplate: (id, templateId) => api.put(`/goae/workflow/${id}/service-template`, { template_id: templateId }),
  quote: (id, body) => api.post(`/goae/workflow/${id}/quote`, body),
  save: (id, body) => api.post(`/goae/workflow/${id}/plan`, body),
  pdf: (id) => api.get(`/goae/workflow/${id}/pdf`, { responseType: 'blob' }),
  printPacket: (id) => api.get(`/goae/workflow/${id}/print-packet`, { responseType: 'blob' }),
  agreement: (id, body) => api.post(`/goae/workflow/${id}/agreement`, body),
  listTreatmentTemplates: () => cachedGet('/goae/treatment-templates'),
  updateTreatmentTemplate: (id, body) => api.put(`/goae/treatment-templates/${id}`, body),
  saveTemplate: (body) => api.post('/goae/treatment-templates', body),
  materialQuote: (body) => api.post('/goae/material-cost-quote', body),
  recordPaper: (id, body) => api.post(`/goae/fee-agreements/${id}/paper`, body),
  approveLateFee: (id, body) => api.post(`/goae/fee-agreements/${id}/late-fee-approval`, body),
  reviewScan: (id, body) => api.post(`/goae/fee-agreements/${id}/review-scan`, body),
};

export const goaeAPI = {
  platformRelease: (config) => api.get('/goae/platform-release', config),
  list: (config) => api.get('/goae/preparations', config),
  listInvoices: (params, config) => cachedGet('/goae/invoices', params, config),
  listCatalog: (params, config) => cachedGet('/goae/catalog', params, config),
  listPractitioners: (config) => cachedGet('/goae/practitioners', undefined, config),
  savePractitioners: (payload, config) => api.put('/goae/practitioners', payload, config),
  listTemplates: (config) => cachedGet('/goae/templates', undefined, config),
  saveTemplates: (payload, config) => api.put('/goae/templates', payload, config),
  createFromAppointment: (appointmentId, config) => api.post(`/goae/preparations/from-appointment/${appointmentId}`, {}, config),
  save: (id, payload, config) => api.put(`/goae/preparations/${id}`, payload, config),
  discard: (id, config) => api.delete(`/goae/preparations/${id}`, config),
  reopen: (id, config) => api.post(`/goae/preparations/${id}/reopen`, {}, config),
  invoiceRequirements: (id, config) => api.get(`/goae/preparations/${id}/invoice-requirements`, config),
  previewInvoice: (id, payload, config) => api.post(`/goae/preparations/${id}/invoice-preview`, payload, { responseType: 'blob', ...(config || {}) }),
  issueInvoice: (id, payload, config) => api.post(`/goae/preparations/${id}/invoice`, payload, config),
  getInvoice: (id, config) => api.get(`/goae/invoices/${id}`, config),
  markInvoicePaid: (id, config) => api.post(`/goae/invoices/${id}/mark-paid`, {}, config),
  cancelInvoice: (id, payload, config) => api.post(`/goae/invoices/${id}/cancel`, payload, config),
  downloadInvoicePdf: (id, config) => api.get(`/goae/invoices/${id}/pdf`, { responseType: 'blob', ...(config || {}) }),
  invoiceDeliveryInfo: (id, config) => api.get(`/goae/invoices/${id}/delivery-info`, config),
  sendInvoiceEmail: (id, recipientEmail, patientEmailAtOpen, config) => api.post(`/goae/invoices/${id}/send-email`, { recipient_email: recipientEmail, patient_email_at_open: patientEmailAtOpen }, config),
  downloadAuditArchive: (params, config) => api.get('/goae/audit-archive', { params, responseType: 'blob', ...(config || {}) }),
  invoiceNumberSettings: (config) => api.get('/goae/invoice-number-settings', config),
  configureInvoiceNumberStart: (firstValue, config) => api.post('/goae/invoice-number-settings', { first_value: firstValue }, config),
  listLegacyInvoices: (params, config) => api.get('/goae/legacy-invoices', { params, ...(config || {}) }),
  importLegacyInvoice: (formData, config) => api.post('/goae/legacy-invoices', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, ...(config || {}),
  }),
  importLegacyInvoicesBulk: (formData, config) => api.post('/goae/legacy-invoices/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, ...(config || {}),
  }),
  downloadLegacyInvoice: (id, config) => api.get(`/goae/legacy-invoices/${id}/file`, { responseType: 'blob', ...(config || {}) }),
  downloadCsv: (id, config) => api.get(`/goae/preparations/${id}/csv`, { responseType: 'blob', ...(config || {}) }),
  downloadPdf: (id, config) => api.get(`/goae/preparations/${id}/pdf`, { responseType: 'blob', ...(config || {}) }),
};

// Honorarvereinbarungen nach § 2 GOÄ
export const feeAgreementAPI = {
  list: (params = {}, config) => api.get('/goae/fee-agreements', { params, ...(config || {}) }),
  create: (payload, config) => api.post('/goae/fee-agreements', payload, config),
  update: (id, payload, config) => api.put(`/goae/fee-agreements/${id}`, payload, config),
  remove: (id, config) => api.delete(`/goae/fee-agreements/${id}`, config),
  signAsPhysician: (id, payload, config) => api.post(`/goae/fee-agreements/${id}/physician-signature`, payload, config),
  share: (id, config) => api.post(`/goae/fee-agreements/${id}/share`, {}, config),
  uploadSignedScan: (id, file, signedOn, config) => {
    const body = new FormData();
    body.append('file', file);
    if (signedOn) body.append('signed_on', signedOn);
    return api.post(`/goae/fee-agreements/${id}/signed-scan`, body, {
      ...(config || {}),
      headers: { ...(config?.headers || {}), 'Content-Type': 'multipart/form-data' },
    });
  },
  handOver: (id, payload, config) => api.post(`/goae/fee-agreements/${id}/handover`, payload, config),
  void: (id, payload, config) => api.post(`/goae/fee-agreements/${id}/void`, payload, config),
  downloadPdf: (id, config) => api.get(`/goae/fee-agreements/${id}/pdf`, { responseType: 'blob', ...(config || {}) }),
};

export const publicFeeAgreementAPI = {
  get: (code, config) => api.get(`/public/fee-agreements/${code}`, config),
  sign: (code, payload, config) => api.post(`/public/fee-agreements/${code}/sign`, payload, config),
  downloadPdf: (code, config) => api.get(`/public/fee-agreements/${code}/pdf`, { responseType: 'blob', ...(config || {}) }),
};

export const estimateAPI = {
  goaeHandoff: (id, payload) => api.post(`/estimates/${id}/goae-handoff`, payload),
  billingContext: id => api.get(`/estimates/${id}/billing-context`),
  invoicePreview: (id, payload) => api.post(`/estimates/${id}/invoice-preview`, payload),
  issueInvoice: (id, payload) => api.post(`/estimates/${id}/invoice`, payload),
  billingPreview: (id, payload) => api.post(`/estimates/${id}/billing-preview`, payload),
  delete: id => api.delete(`/estimates/${id}`).then(res => { cacheInvalidate('/estimates'); return res; }),
  createLink: (id, version) => api.post(`/estimates/${id}/acceptance-link`, {version}),
  sendLink: (id, payload) => api.post(`/estimates/${id}/send-link`, payload),
  list: (params, config) => cachedGet('/estimates', params, config),
  get: id => api.get(`/estimates/${id}`),
  preview: payload => api.post('/estimates/preview', payload),
  save: (id, payload) => api.post(`/estimates/${id}/versions`, payload).then(res => { cacheInvalidate('/estimates'); return res; }),
  accept: (id, payload) => api.post(`/estimates/${id}/accept`, payload).then(res => { cacheInvalidate('/estimates'); return res; }),
  pdf: (id, version) => api.get(`/estimates/${id}/versions/${version}/pdf`, { responseType: 'blob' }),
};

export const patientInvoiceAPI = {
  previewStandalone: (payload, config) => api.post('/invoices/standalone/preview', payload, { responseType: 'blob', ...(config || {}) }),
  issueStandalone: (payload, config) => api.post('/invoices/standalone', payload, config),
  recordStandalonePayment: (id, payload, config) => api.post(`/invoices/standalone/${id}/payments`, payload, config),
  forAppointment: (appointmentId, config) => api.get(`/invoices/appointments/${appointmentId}`, config),
  standardRequirements: (appointmentId, config) => api.get(`/invoices/appointments/${appointmentId}/requirements`, config),
  previewStandard: (appointmentId, payload, config) => api.post(`/invoices/appointments/${appointmentId}/preview`, payload, { responseType: 'blob', ...(config || {}) }),
  issueStandard: (appointmentId, payload, config) => api.post(`/invoices/appointments/${appointmentId}`, payload, config),
};

export const faceMapMaterialsAPI = {
  list: (config) => cachedGet("/face-map-materials", undefined, config),
  create: (payload, config) => api.post("/face-map-materials", payload, config),
  update: (id, payload, config) => api.patch(`/face-map-materials/${id}`, payload, config),
  delete: (id, config) => api.delete(`/face-map-materials/${id}`, config),
  listProofs: (id, config) => api.get(`/face-map-materials/${id}/proofs`, config),
  uploadProof: (id, file, config) => {
    const body = new FormData();
    body.append('file', file);
    return api.post(`/face-map-materials/${id}/proofs`, body, {
      ...(config || {}),
      headers: { ...(config?.headers || {}), 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadProof: (id, proofId, config) => api.get(`/face-map-materials/${id}/proofs/${proofId}`, {responseType:'blob', ...(config || {})}),
};

export const materialBatchesAPI = {
  list: (config) => cachedGet('/material-batches', undefined, config),
  create: (payload, config) => api.post('/material-batches', payload, config),
};

export const treatmentMaterialsAPI = {
  list: (params = {}, config) => api.get("/treatment-materials", { params, ...(config || {}) }),
  create: (payload, config) => api.post("/treatment-materials", payload, config),
  update: (id, payload, config) => api.patch(`/treatment-materials/${id}`, payload, config),
  delete: (id, config) => api.delete(`/treatment-materials/${id}`, config),
  report: (params = {}, config) => cachedGet("/reports/treatment-materials", params, config),
  // Belege zu Auslagen nach § 10 GOÄ
  listReceipts: (id, config) => api.get(`/treatment-materials/${id}/receipts`, config),
  uploadReceipt: (id, file, config) => {
    const body = new FormData();
    body.append("file", file);
    return api.post(`/treatment-materials/${id}/receipts`, body, {
      ...(config || {}),
      headers: { ...(config?.headers || {}), 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadReceipt: (id, receiptId, config) =>
    api.get(`/treatment-materials/${id}/receipts/${receiptId}`, { responseType: "blob", ...(config || {}) }),
  deleteReceipt: (id, receiptId, config) => api.delete(`/treatment-materials/${id}/receipts/${receiptId}`, config),
};

export const messageLogsAPI = {
  list: (params, config) => api.get("/message-logs", { params, ...(config || {}) }),
  retry: (id, config) => api.post(`/message-logs/${id}/retry`, {}, config),
};

export const notificationAPI = {
  getSettings: (params = {}, config) => api.get("/notification-settings", { params, ...(config || {}) }),
  updateSettings: (payload, params = {}, config) => api.put("/notification-settings", payload, { params, ...(config || {}) }),
  getFeed: (params, config) => api.get("/notifications/feed", { params, ...(config || {}) }),
  testSms: (payload, params = {}, config) => api.post("/notification-settings/test-sms", payload, { params, ...(config || {}) }),
  retrySmsBillingSync: (params, config) => api.post("/notification-settings/retry-sms-billing-sync", {}, { params, ...(config || {}) }),
  sendReviewRequest: (payload, config) => api.post("/notification-settings/send-review-request", payload, config),
  listTemplates: (params = {}, config) => api.get("/email-templates", { params, ...(config || {}) }),
  createTemplate: (payload, params = {}, config) => api.post("/email-templates", payload, { params, ...(config || {}) }),
  updateTemplate: (id, payload, params = {}, config) => api.put(`/email-templates/${id}`, payload, { params, ...(config || {}) }),
  deleteTemplate: (id, params = {}, config) => api.delete(`/email-templates/${id}`, { params, ...(config || {}) }),
};

export const apiKeysAPI = {
  list: (config) => api.get("/api-keys", config),
  create: (payload, config) => api.post("/api-keys", payload, config),
  setActive: (id, active, config) => api.patch(`/api-keys/${id}`, { active }, config),
  setExpiry: (id, expiresAt, config) => api.patch(`/api-keys/${id}`, { expires_at: expiresAt }, config),
  clearExpiry: (id, config) => api.patch(`/api-keys/${id}`, { clear_expiry: true }, config),
  delete: (id, config) => api.delete(`/api-keys/${id}`, config),
};

export const webhooksAPI = {
  list: (config) => api.get("/webhooks", config),
  create: (payload, config) => api.post("/webhooks", payload, config),
  update: (id, payload, config) => api.put(`/webhooks/${id}`, payload, config),
  remove: (id, config) => api.delete(`/webhooks/${id}`, config),
  test: (id, config) => api.post(`/webhooks/${id}/test`, {}, config),
};

// Alias falls dein UI noch "bookingsAPI" importiert
export const bookingsAPI = appointmentsAPI;

// -------------------------
// REPORTS (server.py: /reports/overview)
// supports location_id query OR X-Location-Id header
// -------------------------
export const reportsAPI = {
  dashboardMetrics: (params, config) =>
    cachedGet("/dashboard/metrics", params, config),
  overview: (params, config) =>
    cachedGet("/reports/overview", params, config),
  payments: (params, config) =>
    cachedGet("/reports/payments", params, config),
  communications: (params, config) =>
    cachedGet("/reports/communications", params, config),
  staffUtilization: (params, config) =>
    cachedGet("/reports/staff-utilization", params, config),
  bookingStats: (params, config) =>
    cachedGet("/reports/booking-stats", params, config),
  recentActivity: (config) =>
    cachedGet("/reports/recent-activity", undefined, config),
  settlement: (params, config) =>
    cachedGet("/reports/settlement", params, config),
  settlementCsv: (params, config) =>
    api.get("/reports/settlement.csv", { params, responseType: "blob", ...(config || {}) }),
  settlementPdf: (params, config) =>
    api.get("/reports/settlement.pdf", { params, responseType: "blob", ...(config || {}) }),
};

export const patientAcquisitionAPI = {
  overview: (params, config) => cachedGet("/patient-acquisition/overview", params, config),
  intakeLog: (params, config) => api.get("/patient-acquisition/integrations/log", { params, ...(config || {}) }),
  createLeadFromIntake: (eventId) => api.post(`/patient-acquisition/integrations/log/${eventId}/create-lead`),
  // Raeumt einen Lead weg, der beim Einrichten durch eine Testeinsendung entstand.
  deleteIntakeLead: (eventId) => api.delete(`/patient-acquisition/integrations/log/${eventId}/lead`),
  // Eine Adresse, die den Schluessel enthaelt: kein Header, kein "Bearer".
  hookUrl: () => api.get("/patient-acquisition/integrations/hook-url"),
  rotateHookUrl: () => api.post("/patient-acquisition/integrations/hook-url/rotate"),
  selftest: () => api.post("/patient-acquisition/integrations/selftest"),
  // Eigene Zuordnung eingehender Formularfelder. Formulardienste benennen
  // ihre Felder mit der Beschriftung im Formular, die ist pro Praxis anders.
  fieldMapping: () => api.get("/patient-acquisition/integrations/field-mapping"),
  saveFieldMapping: (payload) => api.put("/patient-acquisition/integrations/field-mapping", payload),
  opportunities: (params, config) => cachedGet("/patient-acquisition/opportunities", params, config),
  newInquiryCount: (params, config) => api.get("/patient-acquisition/new-inquiry-count", { params, ...(config || {}) }),
  tasks: (params, config) => api.get("/patient-acquisition/tasks", { params, ...(config || {}) }),
  campaigns: (params, config) => cachedGet("/patient-acquisition/campaigns", params, config),
  createOpportunity: (payload, config) => api.post("/patient-acquisition/opportunities", payload, config),
  getOpportunity: (id, config) => api.get(`/patient-acquisition/opportunities/${id}`, config),
  updateOpportunity: (id, payload, config) => api.patch(`/patient-acquisition/opportunities/${id}`, payload, config),
  deleteOpportunity: (id, config) => api.delete(`/patient-acquisition/opportunities/${id}`, config),
  recordEvent: (id, payload, config) => api.post(`/patient-acquisition/opportunities/${id}/events`, payload, config),
  createTask: (id, payload, config) => api.post(`/patient-acquisition/opportunities/${id}/tasks`, payload, config),
  updateTask: (id, payload, config) => api.patch(`/patient-acquisition/tasks/${id}`, payload, config),
  completeTask: (id, payload, config) => api.post(`/patient-acquisition/tasks/${id}/complete`, payload, config),
  materializeHistorical: (id, config) => api.post(`/patient-acquisition/historical/${String(id).replace(/^historical:/, "")}/materialize`, {}, config),
  journey: (clientId, config) => api.get(`/patient-acquisition/clients/${clientId}/journey`, config),
  runRepeatScheduler: (payload = {}, config) => api.post("/patient-acquisition/repeat/run", payload, config),
};

export const paymentsAPI = {
  get: (id, config) =>
    api.get(`/payments/${id}`, config),
  downloadReceipt: (id, config) =>
    api.get(`/payments/${id}/receipt.pdf`, { responseType: "blob", ...(config || {}) }),
  getDashboardLink: (id, config) =>
    api.get(`/payments/${id}/dashboard-link`, config),
  requestRefund: (id, payload, config) =>
    api.post(`/payments/${id}/refund`, payload, config),
  retryPendingTransfers: (config) =>
    api.post("/payments/retry-pending-transfers", {}, config),
  markLegacyTransferred: (config) =>
    api.post("/payments/mark-legacy-transferred", {}, config),
  getOutstandingFees: (config) =>
    cachedGet("/payments/outstanding-fees", undefined, config),
};


export const lexofficeInvoicesAPI = {
  list: (config) => api.get("/lexoffice-invoices", config),
  downloadPdf: (id, config) =>
    api.get(`/lexoffice-invoices/${id}/pdf`, { responseType: "blob", ...(config || {}) }),
};


export const shopAPI = {
  listOrders: (params, config) => api.get("/shop/orders", { params, ...(config || {}) }),
  summary: (params, config) => api.get("/shop/orders/summary", { params, ...(config || {}) }),
  markViewed: (params, config) => api.post("/shop/orders/mark-viewed", null, { params, ...(config || {}) }),
  shipOrder: (orderId, payload, config) => api.post(`/shop/orders/${orderId}/ship`, payload, config),
};

// -------------------------
// HEALTH / CONFIG (server.py: /health, /config)
// -------------------------
export const metaAPI = {
  health: (config) => api.get("/health", config),
  config: (config) => api.get("/config", config),
};

export const trackingAPI = {
  getSettings: () => api.get('/tracking/settings'),
  saveSettings: (payload) => api.put('/tracking/settings', payload),
  saveAndCheckService: (service, payload) => api.put(`/tracking/settings/${service}`, payload),
  check: () => api.post('/tracking/check'),
};

// -------------------------
// PUBLIC (server.py: /public/*)
// -------------------------
export const publicAPI = {
  getOrg: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}`, { params, ...(config || {}) }),
  getLocations: (orgSlug, config) =>
    api.get(`/public/${orgSlug}/locations`, config),
  getOpeningHours: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/opening-hours`, { params, ...(config || {}) }),
  getServices: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/services`, { params, ...(config || {}) }),
  getProducts: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/products`, { params, ...(config || {}) }),
  createProductOrder: (orgSlug, productId, payload, config) =>
    api.post(`/public/${orgSlug}/products/${productId}/order`, payload, config),
  getStaff: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/staff`, { params, ...(config || {}) }),
  getPolicy: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/policy`, { params, ...(config || {}) }),
  getSlots: (orgSlug, params, config) =>
    api.get(`/public/${orgSlug}/slots`, { params, ...(config || {}) }),
  book: (orgSlug, payload, config) =>
    api.post(`/public/${orgSlug}/book`, payload, config),
  finalizeCheckoutPayment: (checkoutId, sessionId, config) =>
    api.post(`/public/booking-checkouts/${checkoutId}/finalize-payment`, null, {
      params: sessionId ? { session_id: sessionId } : undefined,
      ...(config || {}),
    }),
  cancelCheckoutPayment: (checkoutId, config) =>
    api.post(`/public/booking-checkouts/${checkoutId}/cancel-payment`, null, config),
  joinWaitlist: (orgSlug, payload, config) =>
    api.post(`/public/${orgSlug}/waitlist`, payload, config),
  getAppointmentInfo: (appointmentId, config) =>
    api.get(`/public/appointments/${appointmentId}`, config),
  finalizeAppointmentPayment: (appointmentId, sessionId, config) =>
    api.post(`/public/appointments/${appointmentId}/finalize-payment`, null, {
      params: sessionId ? { session_id: sessionId } : undefined,
      ...(config || {}),
    }),
  cancelAppointmentPayment: (appointmentId, config) =>
    api.post(`/public/appointments/${appointmentId}/cancel-payment`, null, config),
  cancelAppointment: (appointmentId, payload, config) =>
    api.post(`/public/appointments/${appointmentId}/cancel`, payload, config),
};

// -------------------------
// PUBLIC STUDIO API (für /studio/:slug)
// -------------------------
export const publicStudioAPI = {
  getStudio: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}`, config),
  getServices: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/services`, config),
  getProducts: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/products`, config),
  getStaff: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/staff`, config),
  getOpeningHours: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/opening-hours`, config),
  getGallery: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/gallery`, config),
  getGoogleReviews: (studioSlug, config) =>
    api.get(`/public/studio/${studioSlug}/google-reviews`, config),
};

// -------------------------
// STRIPE (server.py: /stripe/create-checkout-session)
// -------------------------
export const stripeAPI = {
  createCheckoutSession: (payload, config) =>
    api.post("/stripe/create-checkout-session", payload, config),
  createProductCheckoutSession: (payload, config) =>
    api.post("/stripe/create-product-checkout-session", payload, config),
  createSubscriptionSession: (payload, config) =>
    api.post("/stripe/create-subscription-session", payload, config),
  createCustomerPortalSession: (payload, config) =>
    api.post("/stripe/customer-portal-session", payload || {}, config),
  getOpenSubscriptionInvoice: (config) =>
    api.get("/stripe/open-subscription-invoice", config),
  updateAddon: (payload, config) =>
    api.post("/stripe/addons", payload, config),
  addonCheckout: (payload, config) =>
    api.post("/stripe/addons/checkout", payload, config),
  connectOnboard: (payload, config) =>
    api.post("/stripe/connect/onboard", payload || {}, config),
  connectStatus: (config) =>
    api.get("/stripe/connect/status", config),
  connectInvoiceDefaults: (config) =>
    api.get("/stripe/connect/invoice-defaults", config),
  connectDashboardLink: (config) =>
    api.get("/stripe/connect/dashboard-link", config),
  connectBalance: (config) =>
    api.get("/stripe/connect/balance", config),
  connectDisconnect: (config) =>
    api.post("/stripe/connect/disconnect", null, config),
};

export const googleCalendarAPI = {
  status: (paramsOrConfig, config) => {
    const looksLikeConfig = paramsOrConfig && (paramsOrConfig.headers || paramsOrConfig.signal);
    return api.get("/integrations/google-calendar/status", looksLikeConfig ? paramsOrConfig : { params: paramsOrConfig, ...(config || {}) });
  },
  externalEvents: (params, config) => api.get("/integrations/google-calendar/external-events", { params, ...(config || {}) }),
  connect: (payload, config) => api.post("/integrations/google-calendar/connect", payload, config),
  disconnect: (params, config) => api.post("/integrations/google-calendar/disconnect", null, { params, ...(config || {}) }),
  syncNow: (params, config) => api.post("/integrations/google-calendar/sync", null, { params, ...(config || {}) }),
};

export const marketingAPI = {
  getEmailSender: (config) => api.get("/marketing/email-sender", config),
  updateEmailSender: (payload, config) => api.put("/marketing/email-sender", payload, config),
  testEmailSender: (payload, config) => api.post("/marketing/email-sender/test", payload, config),
  deleteEmailSender: (config) => api.delete("/marketing/email-sender", config),
  listCampaigns: (config) => cachedGet("/marketing/campaigns", undefined, config),
  createCampaign: (payload, config) => api.post("/marketing/campaigns", payload, config),
  sendCampaignTest: (payload, config) => api.post("/marketing/campaigns/test-send", payload, config),
  updateCampaign: (campaignId, payload, config) => api.put(`/marketing/campaigns/${campaignId}`, payload, config),
  deleteCampaign: (campaignId, config) => api.delete(`/marketing/campaigns/${campaignId}`, config),
  previewCampaign: (payload, config) => api.post("/marketing/campaigns/preview-render", payload, config),
  preflightCampaign: (campaignId, config) => api.post(`/marketing/campaigns/${campaignId}/preflight`, {}, config),
  sendCampaign: (campaignId, payload, config) => api.post(`/marketing/campaigns/${campaignId}/send`, payload, config),
  getCampaignAnalytics: (campaignId, config) => api.get(`/marketing/campaigns/${campaignId}/analytics`, config),
  listAutomations: (config) => cachedGet("/marketing/automations", undefined, config),
  createAutomation: (payload, config) => api.post("/marketing/automations", payload, config),
  updateAutomation: (automationId, payload, config) => api.put(`/marketing/automations/${automationId}`, payload, config),
  deleteAutomation: (automationId, config) => api.delete(`/marketing/automations/${automationId}`, config),
  listTemplates: (config) => cachedGet("/marketing/templates", undefined, config),
  createTemplate: (payload, config) => api.post("/marketing/templates", payload, config),
  updateTemplate: (templateId, payload, config) => api.put(`/marketing/templates/${templateId}`, payload, config),
  deleteTemplate: (templateId, config) => api.delete(`/marketing/templates/${templateId}`, config),
  uploadImage: (file, config) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/marketing/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
  stats: (config) => api.get("/marketing/stats", config),
};

export const messengerAPI = {
  getSummary: (config) => api.get("/messenger/summary", config),
  listThreads: (config) => api.get("/messenger/threads", config),
  createThread: (payload, config) => api.post("/messenger/threads", payload, config),
  listMessages: (threadId, config) => api.get(`/messenger/threads/${threadId}/messages`, config),
  markUnread: (threadId, config) => api.post(`/messenger/threads/${threadId}/mark-unread`, {}, config),
  deleteThread: (threadId, config) => api.delete(`/messenger/threads/${threadId}`, config),
  deleteMessage: (messageId, config) => api.delete(`/messenger/messages/${messageId}`, config),
  sendMessage: (threadId, payload, config) => api.post(`/messenger/threads/${threadId}/messages`, payload, config),
};

export const instagramAPI = {
  getStatus: (config) => api.get("/instagram/status", config),
  getConnectUrl: (config) => api.get("/instagram/connect-url", config),
  disconnect: (config) => api.delete("/instagram/connection", config),
  listAutomations: (config) => cachedGet("/instagram/automations", undefined, config),
  simulateAutomation: (payload, config) => api.post("/instagram/automations/simulate", payload, config),
  publishAutomation: (id, config) => api.post(`/instagram/automations/${id}/publish`, {}, config),
  listAutomationRuns: (id, config) => api.get(`/instagram/automations/${id}/runs`, config),
  createAutomation: (payload, config) => api.post("/instagram/automations", payload, config),
  updateAutomation: (automationId, payload, config) => api.put(`/instagram/automations/${automationId}`, payload, config),
  deleteAutomation: (automationId, config) => api.delete(`/instagram/automations/${automationId}`, config),
  linkContact: (payload, config) => api.post("/instagram/contacts/link", payload, config),
};

const withGlobalStudio = (config = {}) => ({
  ...(config || {}),
  headers: {
    ...(config?.headers || {}),
    "X-Location-Id": "all",
  },
});

export const formsAPI = {
  list: (config) => cachedGet("/forms", undefined, withGlobalStudio(config)),
  create: (payload, config) => api.post("/forms", payload, withGlobalStudio(config)),
  update: (id, payload, config) => api.put(`/forms/${id}`, payload, withGlobalStudio(config)),
  remove: (id, config) => api.delete(`/forms/${id}`, withGlobalStudio(config)),
  previewPdf: (id, config) => api.get(`/forms/${id}/preview-pdf`, { responseType: "blob", ...(config || {}) }),
  listSubmissions: (id, config) => api.get(`/forms/${id}/submissions`, withGlobalStudio(config)),
  // Einreichungen über alle Formulare hinweg – für die Übersichtsseite
  listAllSubmissions: (params, config) => api.get("/forms/submissions", { params, ...withGlobalStudio(config) }),
  getSubmission: (formId, submissionId, config) => api.get(`/forms/${formId}/submissions/${submissionId}`, withGlobalStudio(config)),
  updateSubmission: (formId, submissionId, payload, config) => api.put(`/forms/${formId}/submissions/${submissionId}`, payload, withGlobalStudio(config)),
  downloadSubmissionPdf: (formId, submissionId, config) =>
    api.get(`/forms/${formId}/submissions/${submissionId}/pdf`, { responseType: "blob", ...(config || {}) }),
  downloadBulkPdf: (formId, payload, config) =>
    api.post(`/forms/${formId}/submissions/bulk-pdf`, payload || {}, { responseType: "blob", ...(config || {}) }),
  deleteSubmission: (formId, submissionId, config) => api.delete(`/forms/${formId}/submissions/${submissionId}`, withGlobalStudio(config)),
  share: (id, payload, config) => api.post(`/forms/${id}/share`, payload, config),
  sendEmail: (id, payload, config) => api.post(`/forms/${id}/send-email`, payload, config),
  sendBundleEmail: (payload, config) => api.post("/forms/send-bundle-email", payload, config),
  publicGet: (token, config) => api.get(`/public/forms/${token}`, config),
  publicSubmit: (token, payload, config) => api.post(`/public/forms/${token}`, payload, config),
  publicGetShort: (slugCode, config) => api.get(`/public/forms/short/${slugCode}`, config),
  publicSubmitShort: (slugCode, payload, config) => api.post(`/public/forms/short/${slugCode}`, payload, config),
};

export const chatAPI = {
  sendMessage: (payload, config) => api.post("/chat/message", payload, config),
  sendSupportEmail: (body, config) => api.post('/chat/support-email', body, { ...config, headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } }),
};

export const adManagerAPI = {
  listTemplates: (params, config) => api.get("/ad-manager/templates", { params, ...(config || {}) }),
};

// -------------------------
// ADMIN (Super Admin)
// -------------------------
export const adminAPI = {
  listHelpChats: (params, config) => api.get("/admin/help-chats", { params, ...(config || {}) }),
  getHelpChat: (id, params, config) => api.get(`/admin/help-chats/${encodeURIComponent(id)}`, { params, ...(config || {}) }),
  me: (config) => api.get("/admin/me", config),
  getProviderSignature: (config) => api.get("/admin/provider-signature", config),
  uploadProviderSignature: (file, enabled = false, config) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("enabled", String(Boolean(enabled)));
    return api.post("/admin/provider-signature/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
  setProviderSignatureEnabled: (enabled, config) =>
    api.patch("/admin/provider-signature", { enabled: Boolean(enabled) }, config),
  listOrgs: (params, config) => api.get("/admin/orgs", { params, ...(config || {}) }),
  getOrg: (orgId, config) => api.get(`/admin/orgs/${orgId}`, config),
  getOrgGoaeWorkflow: (orgId, config) => api.get(`/admin/orgs/${orgId}/goae-workflow`, config),
  updateOrgGoaeWorkflow: (orgId, payload, config) => api.put(`/admin/orgs/${orgId}/goae-workflow`, payload, config),
  listOrgUsers: (orgId, config) => api.get(`/admin/orgs/${orgId}/users`, config),
  listOrgAppointments: (orgId, config) => api.get(`/admin/orgs/${orgId}/appointments`, config),
  deleteOrgAppointments: (orgId, payload, config) =>
    api.delete(`/admin/orgs/${orgId}/appointments`, { data: payload, ...(config || {}) }),
  deleteOrg: (orgId, config) => api.delete(`/admin/orgs/${orgId}`, config),
  createOrgVisitSession: (orgId, payload, config) => api.post(`/admin/orgs/${orgId}/visit-session`, payload || {}, config),
  createOrg: (payload, config) => api.post("/admin/orgs", payload, config),
  updateOrg: (orgId, payload, config) => api.patch(`/admin/orgs/${orgId}`, payload, config),
  updateOrgSubscription: (orgId, payload, config) =>
    api.patch(`/admin/orgs/${orgId}/subscription`, payload, config),
  listUsers: (params, config) => api.get("/admin/users", { params, ...(config || {}) }),
  updateUser: (userId, payload, config) => api.patch(`/admin/users/${userId}`, payload, config),
  resetUserPassword: (userId, config) => api.post(`/admin/users/${userId}/reset-password`, null, config),
  deleteUser: (userId, config) => api.delete(`/admin/users/${userId}`, config),
  listSettings: (config) => api.get("/admin/settings", config),
  saveSettings: (payload, config) => api.put("/admin/settings", payload, config),
  listPlans: (config) => api.get("/admin/plans", config),
  savePlans: (payload, config) => api.put("/admin/plans", payload, config),
  resetPlan: (planKey, config) => api.delete(`/admin/plans/${planKey}`, config),
  listAddons: (config) => api.get("/admin/addons", config),
  updateAddon: (addonKey, payload, config) => api.put(`/admin/addons/${addonKey}`, payload, config),
  reportsOverview: (config) => api.get("/admin/reports/overview", config),
  analyticsOverview: (params, config) => api.get("/admin/analytics/overview", { params, ...(config || {}) }),
  exportCsv: (kind, config) =>
    api.get(`/admin/exports/${kind}`, { responseType: "blob", ...(config || {}) }),
  listPayments: (params, config) => api.get("/admin/payments", { params, ...(config || {}) }),
  getPayment: (paymentId, config) => api.get(`/admin/payments/${paymentId}`, config),
  listStripeEvents: (params, config) => api.get("/admin/stripe-events", { params, ...(config || {}) }),
  getStripeEvent: (eventId, config) => api.get(`/admin/stripe-events/${eventId}`, config),
  listPendingLexofficeContacts: (config) => api.get("/admin/lexoffice/pending-contacts", config),
  updatePendingLexofficeBillingDetails: (orgId, payload, config) => api.patch(`/admin/lexoffice/pending-contacts/${orgId}/billing-details`, payload, config),
  listLexofficeSubscriptionMirrors: (params, config) => api.get("/admin/lexoffice/subscription-mirrors", { params, ...(config || {}) }),
  resolveLexofficeContact: (payload, config) => api.post("/admin/lexoffice/resolve-contact", payload, config),
  confirmNewLexofficeContact: (payload, config) => api.post("/admin/lexoffice/confirm-new-contact", payload, config),
  listEnterpriseLeads: (params, config) => api.get("/admin/enterprise-leads", { params, ...(config || {}) }),
  listSoftwareLeads: (params, config) => api.get("/admin/software-leads", { params, ...(config || {}) }),
  updateSoftwareLead: (leadId, payload, config) => api.patch(`/admin/software-leads/${encodeURIComponent(leadId)}`, payload, config),
  retrySoftwareLeadNotification: (leadId, config) => api.post(`/admin/software-leads/${encodeURIComponent(leadId)}/retry-notification`, {}, config),
  listAdTemplates: (config) => api.get("/admin/ad-templates", config),
  createAdTemplate: (payload, config) => api.post("/admin/ad-templates", payload, config),
  updateAdTemplate: (templateId, payload, config) => api.patch(`/admin/ad-templates/${templateId}`, payload, config),
  deleteAdTemplate: (templateId, config) => api.delete(`/admin/ad-templates/${templateId}`, config),
  uploadAdTemplateImage: (file, config) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/admin/ad-templates/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...(config || {}),
    });
  },
  listSubscriptions: (params, config) => api.get("/admin/subscriptions", { params, ...(config || {}) }),
  trialsOverview: (params, config) => api.get("/admin/subscriptions/trials-overview", { params, ...(config || {}) }),
};

// -------------------------
// PLANS (Authenticated)
// -------------------------
export const plansAPI = {
  list: (config) => api.get("/plans", config),
  listPublic: (config) => api.get("/plans/public", config),
};

// Default export (falls du es irgendwo so nutzt)
export default api;

export const privateFilesAPI = {
  upload: async (file, target, targetId, kind = 'image') => {
    const data = new FormData();
    data.append('file', file);
    data.append('target', target);
    data.append('target_id', targetId);
    data.append('kind', kind);
    return (await api.post('/mobile/uploads', data)).data.public_url;
  },
  remove: (url) => api.post('/private-files/delete', { urls: [url] }),
};
